import "./ContainerDetails.scss";
import React, { useEffect, useState, useCallback } from "react";
import {
  JobBreakUpDetail,
  JobOrder,
  JobStatusEnum,
} from "../../interfaces/job.interface";
import {
  containerSizeStringToNumber,
  formatIsoDate,
  getContainerStatus,
  transformTruckNumber,
} from "../../utils/general";
import Modal from "../Modal/Modal";
import { ReactComponent as LocationIcon } from "../../assets/icons/location-pin-icon.svg";
import { ReactComponent as StatusIcon } from "../../assets/icons/job-status-icon.svg";
import { ReactComponent as LocationTickIcon } from "../../assets/icons/location-pin-with-tick.svg";
import noJobInfoGraphics from "./../../assets/images/no-data-infographics.svg";
import LiveTracking from "../LiveTracking/LiveTracking";
import TableComponent from "../TableComponent/TableComponent";
import { TableColumn } from "../TableComponent/TableComponent";
import ContainerType from "../ContainerType/ContainerType";
import { SelectChangeEvent } from "@mui/material";

interface ContainerDetailsProps {
  job: JobOrder;
  hideContainerAssignment?: boolean;
  onAssignRequest?: () => void;
}

const ContainerDetails: React.FC<ContainerDetailsProps> = ({
  job,
  hideContainerAssignment = false,
  onAssignRequest,
}) => {
  const [stats, setStats] = useState({
    totalContainers: 0,
    totalAssigned: 0,
    pendingToAssign: 0,
    assignmentByTransporters: new Map<string, number>(), // Map to store transporter name and assigned count
  });

  const [selectedJobBreakupDetailId, setSelectedJobBreakUpDetailsId] =
    useState<number>();

  const [isTrackingModalOpen, setIsTrackingModalOpen] =
    useState<boolean>(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  const showTracking = (containerDetail: JobBreakUpDetail) => {
    console.log(containerDetail);
    setSelectedJobBreakUpDetailsId(containerDetail.jobBreakupDetailId);
    setIsTrackingModalOpen(true);
  };

  const hideTracking = () => {
    setIsTrackingModalOpen(false);
  };

  useEffect(() => {
    if (job) {
      const assignmentByTransporters = new Map<string, number>();
      let pendingToAssign = 0;
      let totalAssigned = 0;

      const totalContainers = job.jobBreakups.reduce(
        (total, breakup) => total + breakup.count,
        0
      );

      job.jobBreakups.forEach((breakup) => {
        breakup.jobBreakUpDetails.forEach((container: JobBreakUpDetail) => {
          if (container.containerNumber) {
            totalAssigned++;
            assignmentByTransporters.set(
              container.transporterIdName.name,
              (assignmentByTransporters.get(container.containerNumber) || 0) + 1
            );
          } else {
            pendingToAssign++;
          }
        });
      });

      setStats({
        totalContainers,
        totalAssigned,
        pendingToAssign,
        assignmentByTransporters,
      });
    }
  }, [job]);

  const columns: TableColumn[] = [
    {
      header: "Sl No.",
      accessor: "slNo",
      centerAlign: true,
      width: "80px",
    },
    {
      header: "Container Number",
      accessor: "containerNumber",
      sortable: true,
    },
    {
      header: "Type",
      accessor: "sizeAndType",
      sortable: true,
      centerAlign: true,
      width: "100px",
      cell: (value: any, row: any) => {
        const size = containerSizeStringToNumber(row.containerSize);
        if (size === null) {
          return row.sizeAndType;
        }
        return <ContainerType size={size} type={row.containerType} />;
      },
    },
    {
      header: "Truck Number",
      accessor: "truckNumber",
      sortable: true,
    },
    {
      header: "Picked Date",
      accessor: "pickedDate",
      sortable: true,
    },
    {
      header: "Dropped Date",
      accessor: "droppedDate",
      sortable: true,
    },
  ];

  const tableData = job.jobBreakups.flatMap((breakup, index) => {
    const previousCount = job.jobBreakups
      .slice(0, index)
      .reduce(
        (acc, prevBreakup) =>
          acc +
          prevBreakup.jobBreakUpDetails.filter(
            (detail) => detail.containerNumber
          ).length,
        0
      );

    return breakup.jobBreakUpDetails
      .filter((containerDetail) => containerDetail.containerNumber)
      .map((containerDetail, detailIndex) => ({
        slNo: previousCount + detailIndex + 1,
        containerNumber: containerDetail.containerNumber,
        containerSize: breakup.containerSize,
        containerType: breakup.containerType,
        sizeAndType: `${containerSizeStringToNumber(breakup.containerSize)} ${
          breakup.containerType
        }`,
        truckNumber: containerDetail.truckIdName
          ? transformTruckNumber(containerDetail.truckIdName.name)
          : "N/A",
        pickedDate: containerDetail.startDate
          ? formatIsoDate(containerDetail.startDate)
          : "N/A",
        droppedDate: containerDetail.endDate
          ? formatIsoDate(containerDetail.endDate)
          : "N/A",
        jobBreakupDetailId: containerDetail.jobBreakupDetailId,
      }));
  });

  // Calculate total elements and pages based on filtered data
  useEffect(() => {
    const totalItems = tableData.length;
    setTotalElements(totalItems);
    setTotalPages(Math.ceil(totalItems / pageSize));
  }, [tableData, pageSize]);

  const handlePageChange = useCallback(
    (page: number) => {
      if (page >= 1 && page <= totalPages) {
        setCurrentPage(page);
      }
    },
    [totalPages]
  );

  const handlePageSizeChange = useCallback(
    (event: SelectChangeEvent<number>) => {
      setPageSize(Number(event.target.value));
      setCurrentPage(1);
    },
    []
  );

  return (
    <div className="container-details-component">
      <div className="stats">
        <div className="stat-block w-1/3">
          <div className="label">Total Containers</div>
          <div className="value total_value">{stats.totalContainers}</div>
        </div>
        <div className="stat-block  w-1/3">
          <div className="label">Assigned Containers</div>
          <div className="value assigned_value">{stats.totalAssigned}</div>
        </div>
        <div className="stat-block  w-1/3">
          <div className="label">Pending Assignments</div>
          <div className="value pending_value">{stats.pendingToAssign}</div>
        </div>
      </div>

      {(stats.totalAssigned === 0 || stats.pendingToAssign > 0) && (
        <>
          {/* {stats.totalAssigned === 0 && (
            <div className="no_container_data center_floater_container">
              <div className="no_data_wrapper">
                <img
                  className="info_graphics  max-w-[400px]"
                  src={noJobInfoGraphics}
                  alt="No Jobs"
                />
                <div className="msg  max-w-[400px]">
                  Please provide container numbers to initiate pick up
                </div>
              </div>
            </div>
          )} */}

          {job.jobStatus !== JobStatusEnum.CANCELLED &&
            job.jobStatus !== JobStatusEnum.DELETED &&
            !hideContainerAssignment &&
            stats.pendingToAssign > 0 && (
              <div
                className={`flex justify-center ${
                  stats.totalAssigned > 0 ? "mb-[20px]" : ""
                }`}
              >
                <button
                  type="button"
                  className="text-black rounded-[5px] border border-black h-[40px] w-[180px]"
                  onClick={onAssignRequest}
                >
                  Assign Containers
                </button>
              </div>
            )}
        </>
      )}

      {stats.totalAssigned > 0 && (
        <>
          <div className="table-container">
            <TableComponent
              columns={columns}
              data={tableData}
              showCheckboxes={false}
              showSerialNumbers={false}
              showPagination={true}
              pageSize={pageSize}
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              totalElements={totalElements}
              enableClientSideFiltering={true}
              className="containers-table"
            />
          </div>
          {selectedJobBreakupDetailId !== undefined && (
            <Modal
              className="live-tracking-modal"
              title="Live Tracking"
              isOpen={isTrackingModalOpen}
              onClose={() => hideTracking()}
            >
              <LiveTracking
                jobBreakupDetailId={selectedJobBreakupDetailId}
                job={job}
              ></LiveTracking>
            </Modal>
          )}
        </>
      )}
    </div>
  );
};

export default ContainerDetails;
